import React from 'react';
import '../styles/Page.css';
import '../styles/About.css';
// Import leadership photos
import AmberPhoto from '../assets/leadership/photos/AmberLabatt.png';
import ThomasPhoto from '../assets/leadership/photos/ThomasLaBatt.png';
// Import leadership bios (PDFs)
import AmberBio from '../assets/leadership/bios/Ms. Amber D. LaBatt - Biography.pdf';
import ThomasBio from '../assets/leadership/bios/Mr. Thomas E. LaBatt - Biography.pdf';

// Dynamically import all certification logos
const certificationLogos = require.context('../assets/certifications', false, /\.(png|jpe?g|svg)$/);

// ============================================================================
// LEADERSHIP DATA - Edit names, titles, photos, and bios here
// ============================================================================
const leadershipData = [
  {
    name: 'Amber D. LaBatt',
    title: 'Chief Executive Officer, One Aligned, Inc.',
    photo: AmberPhoto,
    bio: AmberBio
  },
  {
    name: 'Thomas E. LaBatt',
    title: 'Chief Transformation Officer, One Aligned, Inc.',
    photo: ThomasPhoto,
    bio: ThomasBio
  }
];

// ============================================================================
// CERTIFICATIONS DATA - Edit titles and descriptions here
// ============================================================================
const certificationsData = [
  {
    imageFile: 'SAM.png',
    title: 'SAM Registered',
    description: 'Active System for Award Management (SAM) registration, enabling us to compete for and execute U.S. federal contracts with full regulatory compliance and procurement readiness.'
  },
  {
    imageFile: 'ASQ_Certified.png',
    title: 'ASQ Certified Six Sigma Black Belt',
    description: 'ASQ Certified Six Sigma Black Belt with advanced expertise in Lean Six Sigma methodologies, data-driven decision making, and leading complex process improvement initiatives across large organizations.'
  },
  {
    imageFile: 'WOSB-Certified.png',
    title: 'Woman-Owned Small Business (WOSB)',
    description: 'Officially certified Woman-Owned Small Business (WOSB), qualifying us for federal set-aside contracts and corporate supplier diversity initiatives.'
  },
  {
    imageFile: 'Veteran-Owned-Certified.jpg',
    title: 'Veteran-Owned Small Business',
    description: 'Recognized Veteran-Owned Small Business bringing disciplined leadership, operational accountability, and mission-focused execution to every engagement.'
  },
  {
    imageFile: 'ICF.png',
    title: 'International Coaching Federation',
    description: 'Aligned with International Coaching Federation (ICF) standards, upholding globally recognized ethics, coaching competencies, and measurable performance outcomes.'
  },
  {
    imageFile: 'Calvert-County-Chamber-of-Commerce.png',
    title: 'Calvert County Chamber of Commerce',
    description: 'Active member of the Calvert County Chamber of Commerce, demonstrating strong local business engagement, regional partnership development, and commitment to economic growth within the community we serve.'
  },
  {
    imageFile: 'Small-Business-Chamber-of-Commerce.png',
    title: 'Small Business Chamber of Commerce',
    description: 'Member of the Small Business Chamber of Commerce, supporting advocacy, professional collaboration, and continued business development initiatives.'
  }
];

// Map certification data to images
const certifications = certificationsData.map(cert => {
  const imageKey = `./${cert.imageFile}`;
  return {
    ...cert,
    src: certificationLogos(imageKey)
  };
});

function About() {
  return (
    <div className="page">
      <div className="page-header">
        <h1>About Us</h1>
        <p>Learn more about our mission and values</p>
      </div>

      <div className="page-content">
        <section className="content-section">
          <h2>Our Story</h2>
          <p>
            At One Aligned, Inc., our story begins with a simple idea — a passion for helping organizations, teams, and individuals reach their full potential. 
            We believe that success comes from alignment: aligning people with purpose, processes with strategy, and technology with opportunity. 
            Through this alignment, we help businesses navigate complexity, embrace change, and achieve meaningful, sustainable results.
          </p>
        </section>

        <section className="content-section">
          <h2>Our Mission</h2>
          <p>
            To guide organizations through today's challenges and tomorrow's transformations by aligning strategy, people, processes, and technology.
          </p>
          <p>
            We aim to be a trusted partner that empowers organizations of every size to operate with clarity, efficiency, and confidence — helping them achieve excellence, foster innovation, and sustain long-term success in an ever-changing world.
          </p>
          <p>
            Through collaboration and alignment, we enable our clients to thrive amid complexity, remain competitive, and stay future-ready in their industries.
          </p>
        </section>
      </div>

      <section className="values-section-full">
        <div className="values-section-content">
          <h2>Our Values</h2>
          <div className="values-grid">
            <div className="value-card">
              <h3>Innovation</h3>
              <p>Constantly pushing boundaries and exploring new possibilities</p>
            </div>
            <div className="value-card">
              <h3>Integrity</h3>
              <p>Maintaining the highest standards of honesty and transparency with our customers</p>
            </div>
            <div className="value-card">
              <h3>Excellence</h3>
              <p>Delivering exceptional quality in everything we do</p>
            </div>
            <div className="value-card">
              <h3>Diversity</h3>
              <p>Drawing from multiple backgrounds and perspectives to achieve extraordinary results</p>
            </div>
            <div className="value-card">
              <h3>Responsiveness</h3>
              <p>We adapt with agility to meet the evolving needs of our clients</p>
            </div>
            <div className="value-card">
              <h3>Accountability</h3>
              <p>We take ownership of our commitments and deliver on our promises</p>
            </div>
          </div>
        </div>

        <div className="certifications-section-content">
          <h2>Certifications & Affiliations</h2>
          <div className="certifications-list">
            {certifications.map((certification, index) => (
              <div key={index} className="certification-item">
                <div className="certification-logo">
                  <img src={certification.src} alt={certification.title} className="certification-img" />
                </div>
                <div className="certification-details">
                  <h3 className="certification-name">{certification.title}</h3>
                  <p className="certification-description">
                    {certification.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="leadership-section">
        <h2 className="leadership-title">Leadership Team</h2>
        <div className="leadership-content">
          <p>
            Our leadership team brings decades of combined experience in technology, 
            business strategy, and organizational development. Together, we guide your 
            organization toward a future of sustainable growth and innovation.
          </p>
        </div>
        
        <div className="leadership-cards">
          {leadershipData.map((leader, index) => (
            <a 
              key={index} 
              href={leader.bio} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="leadership-card"
            >
              <div className="leadership-photo-container">
                <img src={leader.photo} alt={leader.name} className="leadership-photo" />
              </div>
              <div className="leadership-info">
                <h3 className="leadership-name">{leader.name}</h3>
                <p className="leadership-position">{leader.title}</p>
              </div>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}

export default About;
